import { getActionPlanHistogramData } from "../../getActionPlanHistogramData";
import { FoodsIcon, HistogramComparisonChart, WhiteBox } from "@foodpilot/foods";
import { Button, Stack, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { NumericalScoreValue } from "src/api/client-api/olapScores/olapScoresSchema";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import { useEntityScores } from "src/context/Scores/useEntityScores";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";

type HistogramOverviewProps = {
    ssqId: number;
    scoreId: number;
    title: string;
    currentValue?: NumericalScoreValue;
    start: { value: NumericalScoreValue; year: string };
    end: { value: NumericalScoreValue; year: string };
    unit: string;
};
export const HistogramOverview = (props: HistogramOverviewProps) => {
    const { title, start, end, currentValue = null, unit, ssqId, scoreId } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const { currentCampaign } = useFoodpilotContext();
    const { company } = useCompanyMetadata();
    const { getEntityScore } = useEntityScores();
    const improvementType = getEntityScore(scoreId)?.improvement;

    const histogramData = getActionPlanHistogramData({
        start,
        current: currentCampaign ? { year: currentCampaign.year.toString(), value: currentValue } : undefined,
        end,
        isMobile,
    });

    return (
        <WhiteBox
            sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                padding: isMobile ? theme.spacing(2) : theme.spacing(4),
                gap: isMobile ? theme.spacing(2) : theme.spacing(4),
                justifyContent: "space-between",
                border: "none",
                boxShadow: "none",
            }}
        >
            <Stack flexDirection="row" gap={theme.spacing(2)} justifyContent={isMobile ? "center" : "flex-start"}>
                <Typography variant="h4" alignSelf={"center"}>
                    {title}
                </Typography>
            </Stack>
            <HistogramComparisonChart
                data={histogramData}
                unit={unit}
                improvementType={improvementType}
                chartHeight={isMobile ? 250 : 300}
                chartStyling={
                    isMobile ?
                        {
                            margin: {
                                top: 40,
                                right: 10,
                                left: 10,
                                bottom: 10,
                            },
                        }
                    :   undefined
                }
            />
            <Link to={URLHandler.company.actions.planning(company.id, ssqId)}>
                <Button
                    variant="ternary"
                    endIcon={<FoodsIcon size={2} icon="arrowRight" />}
                    sx={{ width: "100%", textAlign: "justify", color: theme.custom.grey[2800] }}
                >
                    <Typography variant="body-bold" sx={{ width: "100%" }}>
                        {t("action_plan.go_to_action_plan", "Access action plan")}
                    </Typography>
                </Button>
            </Link>
        </WhiteBox>
    );
};
